// Google Tag Manager — the marketing-analytics destination for the web app.
//
// Deliberately independent of lib/appsflyer.ts: that module is the AppsFlyer Web
// SDK (+ its GA4 mirror) and carries its own Symphony-only mount gate. GTM is a
// separate destination with its own container, so it loads and reports on every
// mount rather than inheriting that gate.
//
// The container itself is injected by public/gtm.js (a FILE, not inline — React's
// raw-HTML injection prop breaks the build under the Wix Standards inline-script
// rule; same reason public/css-recover.js is served that way).

export const GTM_CONTAINER_ID = 'GTM-MDD5C4';

// The event names the GTM container's triggers fire on. Changing a string here
// silently breaks the tag that listens for it — these are an external contract
// with the container's configuration, not internal identifiers.
export const GtmEvent = {
  startOnboarding: 'Symphony Start On boarding',
  finishOnboarding: 'Symphony Finish On boarding',
  activeUser: 'Symphony Active User',
  packagePickerView: 'Package Picker View Symphony',
  purchasePageView: 'Purchase Page View Symphony',
  purchaseSuccess: 'Purchase Premium Plan Success Symphony',
} as const;

export type GtmEventName = (typeof GtmEvent)[keyof typeof GtmEvent];

type DataLayerEntry = Record<string, unknown> | IArguments;

declare global {
  interface Window {
    dataLayer?: DataLayerEntry[];
  }
}

// Push an event onto the dataLayer. Creates the array when the container script
// hasn't run yet (GTM drains whatever it finds on load), so an event fired during
// a page's own mount effect can't be lost to script-load ordering — the failure
// mode that made every AppsFlyer event on a hard load silently report nothing.
//
// `event` is what makes this an EVENT rather than just data. GTM reserves that key for
// its Custom Event triggers ("a special data layer variable called `event` … used by
// JavaScript event listeners to fire tags"); a push carrying only `event_name` still
// lands in the dataLayer, but matches no trigger and fires no tag — silently, which is
// how it looked correct in Preview while every tag sat waiting. Verified in GTM Preview
// against GTM-MDD5C4: before this, only Firebase/GA4's own `event:` pushes were firing
// and our name showed up merely as a variable hanging off one of them.
//
// `event_name` is kept alongside it: it is the key the container's tags and variables
// were configured against, and dropping it would break them.
export const pushGtmEvent = (eventName: GtmEventName, params: Record<string, unknown> = {}): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, event_name: eventName, ...params });
};
